import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { useDocsStore } from '../../stores/docsStore';
import { Spinner } from '../common/Spinner';
import type { LogDate } from '../../types/docs';

function formatDateLabel(dateStr: string): string {
  const date = parseISO(dateStr);
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'MMM d, yyyy');
}

function groupByMonth(dates: LogDate[]): Map<string, LogDate[]> {
  const groups = new Map<string, LogDate[]>();
  for (const entry of dates) {
    const date = parseISO(entry.date);
    const key = format(date, 'MMMM yyyy');
    const group = groups.get(key);
    if (group) {
      group.push(entry);
    } else {
      groups.set(key, [entry]);
    }
  }
  return groups;
}

export function DateList() {
  const { dates, selectedDate, loading, setSelectedDate } = useDocsStore();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (dates.length === 0) {
    return (
      <div className="px-3 py-8 text-center">
        <p className="text-sm text-text-muted">No daily logs found</p>
      </div>
    );
  }

  const grouped = groupByMonth(dates);

  return (
    <div className="overflow-y-auto">
      {Array.from(grouped.entries()).map(([month, entries]) => (
        <div key={month}>
          <div className="px-3 py-2 text-xs font-medium text-text-muted uppercase tracking-wider sticky top-0 bg-bg-primary/95 backdrop-blur-sm">
            {month}
          </div>
          {entries.map((entry) => (
            <button
              key={entry.date}
              onClick={() => setSelectedDate(entry.date)}
              className={`w-full text-left py-2 px-3 text-sm transition-colors ${
                selectedDate === entry.date
                  ? 'bg-accent-purple/10 text-accent-purple-light border-l-2 border-accent-purple'
                  : 'text-text-secondary hover:bg-white/5 border-l-2 border-transparent'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate">{formatDateLabel(entry.date)}</span>
                <span className="flex-shrink-0 text-xs text-text-muted bg-bg-tertiary rounded-full px-2 py-0.5">
                  {entry.entryCount} {entry.entryCount === 1 ? 'entry' : 'entries'}
                </span>
              </div>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

export default DateList;
